import { Color } from './java/Color';
import { Font, Graphics2D, RenderingHints } from './java/Graphics2D';
import { MouseEvent } from './java/AwtEvent';
import { Game } from './Game';
import { Handler } from './Handler';
import { HUD } from './HUD';
import { PageRenderer } from './PageRenderer';

export class Shop {

  private handler: Handler;
  private hud: HUD;

  private healthUpgradeCost = 1000;
  private speedUpgradeCost = 750;
  private refillHealthCost = 750;

  reset(): void {
    this.healthUpgradeCost = 1000;
    this.speedUpgradeCost = 750;
    this.refillHealthCost = 750;
  }

  // Card layout
  private static readonly CARD_W = 320;
  private static readonly CARD_H = 200;
  private static readonly CARD_Y = 180;
  private static readonly CARD_GAP = 40;
  private static readonly TOTAL_W = Shop.CARD_W * 3 + Shop.CARD_GAP * 2;

  private static cardStartX(): number { return Math.trunc((Game.WIDTH - Shop.TOTAL_W) / 2); }
  private static card1X(): number { return Shop.cardStartX(); }
  private static card2X(): number { return Shop.cardStartX() + Shop.CARD_W + Shop.CARD_GAP; }
  private static card3X(): number { return Shop.cardStartX() + (Shop.CARD_W + Shop.CARD_GAP) * 2; }

  // Preview types
  private static readonly PREVIEW_HEALTH = 0;
  private static readonly PREVIEW_SPEED = 1;
  private static readonly PREVIEW_REFILL = 2;

  // Hover state
  private cardHover: number[] = [0, 0, 0];
  private mouseX = 0;
  private mouseY = 0;
  private frameTick = 0;

  constructor(handler: Handler, hud: HUD) {
    this.handler = handler;
    this.hud = hud;
  }

  render(g: Graphics2D): void {
    const g2: Graphics2D = g;
    g2.setRenderingHint(RenderingHints.KEY_TEXT_ANTIALIASING, RenderingHints.VALUE_TEXT_ANTIALIAS_ON);
    g2.setRenderingHint(RenderingHints.KEY_ANTIALIASING, RenderingHints.VALUE_ANTIALIAS_ON);

    PageRenderer.drawBackground(g2);
    PageRenderer.drawTitle(g2, 'Shop');

    // Update hover
    this.updateHover();

    // Points display
    g2.setFont(PageRenderer.SUBTITLE_FONT);
    g2.setColor(PageRenderer.ACCENT);
    const pts = this.hud.getPoints() + ' points';
    let fm = g2.getFontMetrics();
    g2.drawString(pts, Math.trunc((Game.WIDTH - fm.stringWidth(pts)) / 2), 130);

    this.drawCard(g2, Shop.card1X(), Shop.CARD_Y, 'Upgrade Health', 'Extends and refills your health bar.',
      this.healthUpgradeCost, this.hud.getPoints() >= this.healthUpgradeCost, PageRenderer.SUCCESS, this.cardHover[0],
      this.hud.getHealthUpgrades(), Shop.PREVIEW_HEALTH);
    this.drawCard(g2, Shop.card2X(), Shop.CARD_Y, 'Upgrade Speed', 'Increases your movement speed.',
      this.speedUpgradeCost, this.hud.getPoints() >= this.speedUpgradeCost, PageRenderer.ACCENT, this.cardHover[1],
      this.hud.getSpeedUpgrades(), Shop.PREVIEW_SPEED);
    this.drawCard(g2, Shop.card3X(), Shop.CARD_Y, 'Refill Health', 'Fully restores your health bar.',
      this.refillHealthCost, this.hud.getPoints() >= this.refillHealthCost, PageRenderer.WARNING, this.cardHover[2],
      this.hud.getRefills(), Shop.PREVIEW_REFILL);

    // Footer
    g2.setFont(PageRenderer.BODY_FONT);
    g2.setColor(PageRenderer.TEXT_MUTED);
    const footer = 'Press Space to return to game';
    fm = g2.getFontMetrics();
    g2.drawString(footer, Math.trunc((Game.WIDTH - fm.stringWidth(footer)) / 2), Game.HEIGHT - 50);
  }

  private updateHover(): void {
    this.frameTick++;
    const cardXs: number[] = [Shop.card1X(), Shop.card2X(), Shop.card3X()];
    for (let i = 0; i < 3; i++) {
      const over = this.mouseX >= cardXs[i] && this.mouseX <= cardXs[i] + Shop.CARD_W
        && this.mouseY >= Shop.CARD_Y && this.mouseY <= Shop.CARD_Y + Shop.CARD_H;
      const target = over ? 1 : 0;
      this.cardHover[i] += (target - this.cardHover[i]) * 0.14;
      if (Math.abs(this.cardHover[i] - target) < 0.01) this.cardHover[i] = target;
    }
  }

  private drawCard(g: Graphics2D, x: number, y: number, title: string, desc: string,
                   cost: number, canAfford: boolean, accent: Color, hover: number,
                   tier: number, previewType: number): void {
    const maxed = tier >= HUD.MAX_TIER;

    // Card background with hover
    const bg = PageRenderer.lerp(PageRenderer.SURFACE, new Color(30, 42, 58), hover);
    const border = PageRenderer.lerp(PageRenderer.BORDER, maxed ? accent : PageRenderer.BORDER, maxed ? 0.3 : hover * 0.4);
    g.setColor(bg);
    g.fillRoundRect(x, y, Shop.CARD_W, Shop.CARD_H, PageRenderer.R, PageRenderer.R);
    g.setColor(border);
    g.drawRoundRect(x, y, Shop.CARD_W, Shop.CARD_H, PageRenderer.R, PageRenderer.R);

    // Accent top strip
    g.setColor(maxed ? accent : (canAfford ? accent : PageRenderer.TEXT_MUTED));
    g.fillRoundRect(x, y, Shop.CARD_W, 4, 4, 4);

    // Title
    g.setFont(PageRenderer.HEADING_FONT);
    g.setColor(PageRenderer.lerp(PageRenderer.TEXT, accent, hover * 0.3));
    g.drawString(title, x + 20, y + 40);

    // Tier dots
    const dotX = x + 20;
    const dotY = y + 55;
    const dotSize = 8;
    const dotGap = 3;
    for (let i = 0; i < HUD.MAX_TIER; i++) {
      g.setColor(i < tier ? accent : new Color(30, 38, 52));
      g.fillRoundRect(dotX + i * (dotSize + dotGap), dotY, dotSize, dotSize, 3, 3);
    }
    if (maxed) {
      g.setFont(new Font('Arial', Font.BOLD, 10));
      g.setColor(accent);
      g.drawString('MAX', dotX + HUD.MAX_TIER * (dotSize + dotGap) + 4, dotY + 8);
    }

    // Description
    g.setFont(PageRenderer.BODY_FONT);
    g.setColor(PageRenderer.TEXT_SEC);
    g.drawString(desc, x + 20, y + 88);

    if (maxed) {
      // Maxed state
      g.setFont(PageRenderer.HEADING_FONT);
      g.setColor(accent);
      g.drawString('Fully upgraded', x + 20, y + 148);
    } else {
      // Cost
      g.setFont(PageRenderer.LABEL_FONT);
      g.setColor(PageRenderer.TEXT_MUTED);
      g.drawString('COST', x + 20, y + 120);
      g.setFont(PageRenderer.HEADING_FONT);
      g.setColor(canAfford ? accent : PageRenderer.TEXT_MUTED);
      g.drawString(String(cost), x + 20, y + 148);

      // Buy button
      if (canAfford) {
        const buyBg = PageRenderer.lerp(accent, new Color(
          Math.min(accent.getRed() + 30, 255),
          Math.min(accent.getGreen() + 30, 255),
          Math.min(accent.getBlue() + 30, 255)), hover);
        g.setColor(buyBg);
        g.fillRoundRect(x + Shop.CARD_W - 70, y + Shop.CARD_H - 40, 50, 24, 6, 6);
        g.setFont(PageRenderer.SMALL_FONT);
        g.setColor(PageRenderer.BG_DARK);
        g.drawString('Buy', x + Shop.CARD_W - 58, y + Shop.CARD_H - 23);
      }
    }

    // Preview animation — visible when hovering
    if (hover > 0.1) {
      const px = x + Shop.CARD_W - 90;
      const py = y + 100;
      const h = hover;
      const t = this.frameTick * 0.05;

      switch (previewType) {
        case Shop.PREVIEW_HEALTH:
          this.drawHealthPreview(g, px, py, accent, h, t);
          break;
        case Shop.PREVIEW_SPEED:
          this.drawSpeedPreview(g, px, py, accent, h, t);
          break;
        case Shop.PREVIEW_REFILL:
          this.drawRefillPreview(g, px, py, accent, h, t);
          break;
      }
    }
  }

  private drawHealthPreview(g: Graphics2D, x: number, y: number, accent: Color, hover: number, t: number): void {
    const alpha = Math.trunc(hover * 200);

    // Mini health bar background
    const barW = 70, barH = 10;
    g.setColor(new Color(22, 30, 44, alpha));
    g.fillRoundRect(x, y, barW, barH, 5, 5);

    // Current fill
    const pct = HUD.HEALTH / (100 + this.hud.bounds / 2);
    const fillW = Math.trunc(barW * pct);
    g.setColor(new Color(accent.getRed(), accent.getGreen(), accent.getBlue(), alpha));
    g.fillRoundRect(x, y, fillW, barH, 5, 5);

    // Pulsing extension showing the upgrade effect
    const pulse = Math.sin(t * 4) * 0.5 + 0.5;
    const extW = 8 + Math.trunc(pulse * 6);
    const extAlpha = Math.trunc(hover * pulse * 160);
    g.setColor(new Color(accent.getRed(), accent.getGreen(), accent.getBlue(), extAlpha));
    g.fillRoundRect(x + fillW, y, extW, barH, 5, 5);

    // "+" icon
    g.setColor(new Color(accent.getRed(), accent.getGreen(), accent.getBlue(), Math.trunc(hover * 180)));
    g.fillRect(x + barW + 10, y + 2, 8, 2);
    g.fillRect(x + barW + 13, y - 1, 2, 8);

    // Border
    g.setColor(new Color(40, 52, 70, alpha));
    g.drawRoundRect(x, y, barW + extW, barH, 5, 5);
  }

  private drawSpeedPreview(g: Graphics2D, x: number, y: number, accent: Color, hover: number, t: number): void {
    const alpha = Math.trunc(hover * 180);
    const cx = x + 35, cy = y + 5;

    // Mini player square
    g.setColor(new Color(230, 234, 240, alpha));
    g.fillRoundRect(cx - 6, cy - 6, 12, 12, 4, 4);

    // Motion lines — stream backward from the player
    for (let i = 0; i < 4; i++) {
      const offset = ((t * 8 + i * 1.5) % 6);
      const lineX = cx - 12 - Math.trunc(offset * 6);
      const lineAlpha = Math.trunc(hover * (1 - offset / 6) * 140);
      const lineW = 6 + Math.trunc((1 - offset / 6) * 8);
      g.setColor(new Color(accent.getRed(), accent.getGreen(), accent.getBlue(),
        Math.max(0, Math.min(255, lineAlpha))));
      g.fillRect(lineX, cy - 1 + (i - 2) * 4, lineW, 2);
    }

    // Speed arrow
    g.setColor(new Color(accent.getRed(), accent.getGreen(), accent.getBlue(), alpha));
    g.fillPolygon(
      [cx + 14, cx + 22, cx + 14],
      [cy - 5, cy, cy + 5], 3);
  }

  private drawRefillPreview(g: Graphics2D, x: number, y: number, accent: Color, hover: number, t: number): void {
    const alpha = Math.trunc(hover * 200);

    // Mini health bar background
    const barW = 70, barH = 10;
    g.setColor(new Color(22, 30, 44, alpha));
    g.fillRoundRect(x, y, barW, barH, 5, 5);

    // Animated fill — sweeps from left to full
    let fillPct = ((t * 2) % 3);
    if (fillPct > 1) fillPct = 1; // hold at full briefly
    const fillW = Math.trunc(barW * fillPct);
    const fillColor = PageRenderer.lerp(
      new Color(235, 87, 87), accent, fillPct);
    g.setColor(new Color(fillColor.getRed(), fillColor.getGreen(), fillColor.getBlue(), alpha));
    g.fillRoundRect(x, y, fillW, barH, 5, 5);

    // Sparkle at the fill edge
    if (fillPct > 0.2 && fillPct < 1) {
      const sparkle = Math.sin(t * 12) * 0.5 + 0.5;
      const sparkAlpha = Math.trunc(sparkle * hover * 200);
      g.setColor(new Color(255, 255, 255, Math.min(sparkAlpha, 255)));
      g.fillOval(x + fillW - 3, y + 1, 6, 6);
    }

    // Full indicator flash
    if (fillPct >= 1) {
      const flash = Math.sin(t * 6) * 0.3 + 0.7;
      g.setColor(new Color(accent.getRed(), accent.getGreen(), accent.getBlue(),
        Math.trunc(flash * hover * 80)));
      g.fillRoundRect(x, y, barW, barH, 5, 5);
    }

    // Border
    g.setColor(new Color(40, 52, 70, alpha));
    g.drawRoundRect(x, y, barW, barH, 5, 5);
  }

  mouseMoved(e: MouseEvent): void {
    this.mouseX = Game.toGameX(e.getX());
    this.mouseY = Game.toGameY(e.getY());
  }

  mouseDragged(e: MouseEvent): void {
    this.mouseX = Game.toGameX(e.getX());
    this.mouseY = Game.toGameY(e.getY());
  }

  mousePressed(e: MouseEvent): void {
    const mx = Game.toGameX(e.getX());
    const my = Game.toGameY(e.getY());

    if (this.hitCard(mx, my, Shop.card1X()) && this.hud.getPoints() >= this.healthUpgradeCost
      && this.hud.getHealthUpgrades() < HUD.MAX_TIER) {
      this.hud.setPoints(this.hud.getPoints() - this.healthUpgradeCost);
      this.healthUpgradeCost += 250;
      this.hud.bounds += 20;
      HUD.HEALTH = 100 + Math.trunc(this.hud.bounds / 2);
      this.hud.addHealthUpgrade();
    }

    if (this.hitCard(mx, my, Shop.card2X()) && this.hud.getPoints() >= this.speedUpgradeCost
      && this.hud.getSpeedUpgrades() < HUD.MAX_TIER) {
      this.hud.setPoints(this.hud.getPoints() - this.speedUpgradeCost);
      this.speedUpgradeCost += 250;
      this.handler.spd++;
      this.hud.addSpeedUpgrade();
    }

    if (this.hitCard(mx, my, Shop.card3X()) && this.hud.getPoints() >= this.refillHealthCost
      && this.hud.getRefills() < HUD.MAX_TIER) {
      this.hud.setPoints(this.hud.getPoints() - this.refillHealthCost);
      this.refillHealthCost += 250;
      HUD.HEALTH = 100 + Math.trunc(this.hud.bounds / 2);
      this.hud.addRefill();
    }
  }

  private hitCard(mx: number, my: number, cardX: number): boolean {
    return mx >= cardX && mx <= cardX + Shop.CARD_W && my >= Shop.CARD_Y && my <= Shop.CARD_Y + Shop.CARD_H;
  }
}
